import React from "react";
import { IMAGES, LODGES } from "@/lib/site";
import { useContent } from "@/lib/content";
import { LuxeOverline, LuxeDivider } from "./LuxeOverline";

const lodgeFeatures = [
  "Private deck · a stone’s throw from the fishing lake",
  "Fully-fitted kitchen & flat-screen TV",
  "Bedding, towels & linen included",
  "Free fishing access included",
  "Dog-friendly · well-behaved pups welcome",
];

const touringFeatures = [
  "Half-mile enclosed dog walk on-site",
  "Modern shower blocks & drying room",
  "One-night minimum · Dogs welcome",
];

const Stays = () => {
  const c = useContent();
  const lodges = LODGES.map((l) => {
    const key = l.id.replace("lodge-", "");
    return {
      ...l,
      name: c(`lodges.${key}.name`, l.name),
      tag: c(`lodges.${key}.tag`, l.tag),
      image: c(`lodges.${key}.image`) || l.image,
    };
  });
  return (
    <section
      id="stays"
      data-testid="stays-section"
      className="relative py-24 md:py-36 bg-[#F5EFE6] overflow-hidden"
    >
      {/* Subtle rose-gold radial glow */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(228,178,168,0.18) 0%, transparent 55%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-6">
            <LuxeOverline>{c("stays.overline", "Three lodges")}</LuxeOverline>
            <h2 className="font-serif text-4xl md:text-6xl mt-8 text-[#14100F] leading-[1.0]">
              {c("stays.title_pre", "Named after the birds")}{" "}
              <em className="italic rose-gold-text">{c("stays.title_em", "around us")}</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-8">
            <p className="text-[#3A322E] text-base md:text-lg leading-relaxed">
              {c(
                "stays.body",
                "Each lodge sits on the water's edge with its own deck, and every booking includes free access to Walnut & Daisy lakes. Two-night minimum stay across the season."
              )}
            </p>
          </div>
        </div>

        <LuxeDivider className="mb-14" />

        {/* Lodges grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
          data-testid="lodges-grid"
        >
          {lodges.map((lodge) => (
            <div
              key={lodge.id}
              data-testid={`lodge-card-${lodge.id}`}
              className="group relative bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:border-[#B76E79]/40"
            >
              {/* Top rose-gold accent bar (appears on hover) */}
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#B76E79] via-[#E4B2A8] to-[#C9A47E] opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
              <div className="img-frame aspect-[4/3] rounded-none">
                <img src={lodge.image} alt={lodge.name} loading="lazy" />
              </div>
              <div className="p-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-3xl text-[#14100F] leading-tight">
                      {lodge.name}
                    </h3>
                    <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#6A5F58]">
                      {lodge.tag}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#B76E79]">
                      From
                    </div>
                    <div className="font-serif text-4xl rose-gold-text leading-none">
                      £{lodge.price}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#6A5F58] mt-1">
                      / night
                    </div>
                    <div className="text-[10px] text-[#8A7E75] mt-1.5 leading-snug max-w-[110px]">
                      Based on 2 guests
                    </div>
                  </div>
                </div>

                <ul className="mt-6 space-y-2.5">
                  {lodgeFeatures.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-sm text-[#3A322E] leading-relaxed"
                    >
                      <span className="mt-2 w-1.5 h-1.5 rotate-45 rose-gold-bg flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Extra person pricing note */}
                <div className="mt-5 px-3 py-2 rounded-lg bg-[#F5EFE6] border border-[#DDD1BE] text-[11px] text-[#6A5F58] leading-relaxed">
                  <span className="text-[#B76E79] font-semibold">+£15</span> per extra guest per stay · max 4 guests
                </div>

                <div className="mt-5 pt-5 border-t border-[#DDD1BE] flex items-center justify-between">
                  <a
                    href="#book"
                    data-testid={`stay-enquire-${lodge.id}`}
                    className="text-sm font-medium text-[#0F0E0E] link-underline group-hover:text-[#B76E79] transition-colors"
                  >
                    Reserve {lodge.name}
                  </a>
                  <span className="text-[#B76E79] transform group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Touring row */}
        <div className="mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <LuxeOverline>{c("touring.overline", "Camping & touring")}</LuxeOverline>
            <h3 className="font-serif text-3xl md:text-5xl mt-8 text-[#14100F] leading-[1.0]">
              {c("touring.title_pre", "Pitches from")}{" "}
              <span className="rose-gold-text">{c("touring.title_em", "£30 a night")}</span>
            </h3>
            <p className="mt-4 text-sm uppercase tracking-[0.28em] text-[#B76E79] font-semibold">
              Electricity included — 15 kWh/day on EHU pitches
            </p>
            <p className="mt-6 text-[#3A322E] text-base leading-relaxed whitespace-pre-line">
              {c(
                "touring.body",
                "16 hardstanding lake view pitches with EHU\n12 grass pitches with EHU\n20 non-electric pitches\nEHU includes 15 kWh/day — extra use 33p/kWh by prepaid top-up card"
              )}
            </p>

            <ul className="mt-6 space-y-2.5">
              {touringFeatures.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-3 text-sm text-[#3A322E] leading-relaxed"
                >
                  <span className="mt-2 w-1.5 h-1.5 rotate-45 rose-gold-bg flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <a href="#book" data-testid="stay-enquire-touring" className="btn-luxe font-brand tracking-[0.18em]">
                Book a pitch
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              {/* Rose-gold frame */}
              <div className="absolute -inset-2 border border-[#B76E79]/30 rounded-2xl pointer-events-none" />
              <div className="img-frame aspect-[16/11] relative">
                <img src={c("touring.image") || IMAGES.caravanPitches} alt="Touring pitches by the lake" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stays;
